// src/components/consent/CookiePreferencesModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { useCookieConsent } from "../../context/CookieConsentProvider";

export default function CookiePreferencesModal() {
  const { consent, updateConsent, isPrefsOpen, closePreferences } = useCookieConsent();
  const [local, setLocal] = useState(consent);

  const panelRef = useRef(null);

  useEffect(() => {
    setLocal(consent);
  }, [consent, isPrefsOpen]);

  // ✅ ESC to close + focus panel for better mobile behavior
  useEffect(() => {
    if (!isPrefsOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closePreferences();
    };

    window.addEventListener("keydown", onKeyDown);

    // focus the panel so scroll works reliably on iOS/Android
    setTimeout(() => panelRef.current?.focus(), 0);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPrefsOpen, closePreferences]);

  if (!isPrefsOpen) return null;

  const toggle = (key) => setLocal((prev) => ({ ...prev, [key]: !prev[key] }));

  const save = () => {
    updateConsent({
      preferences: !!local.preferences,
      analytics: !!local.analytics,
      marketing: !!local.marketing,
    });
    closePreferences();
  };

  const acceptAllNow = () => {
    updateConsent({ preferences: true, analytics: true, marketing: true });
    closePreferences();
  };

  const rejectAllNow = () => {
    updateConsent({ preferences: false, analytics: false, marketing: false });
    closePreferences();
  };

  return (
    // ✅ Overlay: click outside closes
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => {
        // close only if user clicks the overlay, not the panel
        if (e.target === e.currentTarget) closePreferences();
      }}
    >
      {/* ✅ Center wrapper + safe scrolling */}
      <div className="h-full w-full p-4 flex items-center justify-center">
        {/* ✅ Panel: scrollable if content exceeds screen */}
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          className="
            w-full max-w-xl rounded-3xl border border-pink-400/30 bg-[#11111f] text-white shadow-2xl
            max-h-[85dvh] max-h-[85vh] overflow-y-auto
            outline-none
          "
          onMouseDown={(e) => e.stopPropagation()} // don't close when clicking inside
        >
          {/* ✅ Header stays visible while scrolling inside panel */}
          <div className="sticky top-0 z-10 bg-[#11111f] border-b border-white/10 px-6 py-5 rounded-t-3xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-pink-300">Cookie preferences</h2>
                <p className="mt-1 text-sm text-gray-300">
                  Choose which cookies you allow. Necessary cookies are always on.
                </p>
              </div>

              <button
                type="button"
                onClick={closePreferences}
                className="rounded-full border border-white/20 px-3 py-1 text-sm hover:bg-white/5"
              >
                Close
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <div className="space-y-4">
              <PrefRow
                title="Necessary"
                desc="Required for security, login, and core site functionality."
                checked
                disabled
              />
              <PrefRow
                title="Preferences"
                desc="Remembers settings like language and UI preferences."
                checked={!!local.preferences}
                onChange={() => toggle("preferences")}
              />
              <PrefRow
                title="Analytics"
                desc="Helps us understand usage to improve the product."
                checked={!!local.analytics}
                onChange={() => toggle("analytics")}
              />
              <PrefRow
                title="Marketing"
                desc="Used to personalize ads and measure campaigns."
                checked={!!local.marketing}
                onChange={() => toggle("marketing")}
              />
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={rejectAllNow}
                className="rounded-full border border-pink-400/40 bg-transparent px-5 py-2 font-semibold text-pink-200 hover:bg-white/5"
              >
                Reject all
              </button>

              <button
                type="button"
                onClick={acceptAllNow}
                className="rounded-full bg-[#EB6AB5] px-5 py-2 font-semibold text-black hover:opacity-95"
              >
                Accept all
              </button>

              <button
                type="button"
                onClick={save}
                className="rounded-full border border-pink-400/30 bg-transparent px-5 py-2 font-semibold text-pink-200 hover:bg-white/5"
              >
                Save preferences
              </button>
            </div>

            <div className="mt-4 text-xs text-gray-400">
              You can change this anytime via “Cookie Settings” in the header.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
      <div>
        <div className="font-semibold">{title}</div>
        <div className="mt-1 text-sm text-gray-300">{desc}</div>
      </div>

      <input
        type="checkbox"
        className="h-5 w-5 accent-pink-400"
        checked={!!checked}
        disabled={!!disabled}
        onChange={onChange}
      />
    </div>
  );
}